import { ChildProfile } from '../types';
import { getSupabase } from './supabaseClient';
import { debugLog } from '../utils/debugLogger';

const TABLE_NAME = 'child_profiles';
const TIMEOUT_LIMIT = 12000; 

export const profileService = {
  
  async getProfile(userId: string): Promise<ChildProfile | null> {
    try {
      debugLog("PROFILE", "Fetching profile", { userId });
      const { data, error } = await getSupabase()
        .from(TABLE_NAME)
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      return {
        userId: data.user_id,
        name: data.name,
        authorName: data.author_name || data.name,
        age: data.age,
        sex: data.sex,
        appearance: data.appearance,
        photoBase64: data.photo_base64
      };
    } catch (e) {
      console.error("Supabase Profile Load Failed:", e);
      return null;
    }
  },

  async saveProfile(profile: ChildProfile): Promise<void> {
    const sb = getSupabase();
    
    // 1. Critical Session Check
    // RLS fails if the session is stale. We fetch a fresh session ID.
    const { data: { session } } = await sb.auth.getSession();
    
    if (!session || !session.user) {
      throw new Error("Your session has expired. Please log in again to save your profile.");
    }

    // 2. Strict ID Alignment
    // Ensure the payload user_id matches the authenticated user_id
    if (profile.userId !== session.user.id) {
      debugLog("PROFILE", "ID Mismatch detected", { profileId: profile.userId, sessionId: session.user.id });
      profile.userId = session.user.id; 
    }

    const payload = {
      user_id: profile.userId,
      name: profile.name,
      author_name: profile.authorName,
      age: profile.age,
      sex: profile.sex,
      appearance: profile.appearance,
      photo_base64: profile.photoBase64 || null, 
      updated_at: new Date().toISOString()
    };

    const timeout = (ms: number, msg: string) => new Promise((_, reject) => 
      setTimeout(() => reject(new Error(msg)), ms)
    );

    debugLog("PROFILE", "Saving profile to Supabase", { userId: payload.user_id, hasPhoto: !!payload.photo_base64 });

    try {
      // 3. Execution with Timeout
      const { data, error } = await Promise.race([
        sb.from(TABLE_NAME).upsert(payload, { onConflict: 'user_id' }).select().single(),
        timeout(TIMEOUT_LIMIT, "The magic forge is taking too long to respond. Please check your connection.")
      ]) as any;

      if (error) {
        // 4. Detailed Error Handling
        if (error.code === '42501') {
          throw new Error("Database Permission Error: Please ensure you have configured the RLS policies in Supabase SQL Editor.");
        }
        if (error.message?.toLowerCase().includes('payload too large') || error.code === '413') {
          throw new Error("The photo is too large for the database. Please try a smaller image or lower quality.");
        }
        throw new Error(error.message || "We encountered a problem saving your profile.");
      }

      if (!data) {
        throw new Error("Save confirmation was not received from the server.");
      }
      
      debugLog("PROFILE", "Save and Readback successful", { userId: data.user_id });
    } catch (err: any) {
      console.error("Supabase Save Error Details:", err);
      throw err;
    }
  },

  async deleteProfile(userId: string): Promise<void> {
    try {
      await getSupabase()
        .from(TABLE_NAME)
        .delete()
        .eq('user_id', userId);
    } catch (e) {
      console.error("Supabase Profile Delete Failed:", e);
    }
  }
};